import React, { useState, useContext } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";

import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { dateFormat, trimString } from "../tools";
import AppContext from "../appContext";
import * as api from "../services";
import MutationMessage from "../Components/MutationMessage";
import RenameDialog from "../Components/RenameDialog";
import useAccountSettings from "../ManageAccount/useAccountSettings";
import useInteraction from "../UserSurveys/useInteractions";
import ConflictDialog from "./ConflictDialog";
import useInteractionTypes from "./useInteractionTypes";
import { Box } from "@mui/system";
import useScheduler from "../Scheduler/useScheduler";
import useSaveMutation from "../Scheduler/useSaveMutation";
import moment from "moment";

function UseSurveyButton({
  from,
  interactionId,
  questionList,
  isError = false,
  onError = () => false, // void
  onButtonClick = () => false, // void
  onItemClick = (optionId) => false, // void, optionId is optional
  onSuccess = (data) => false, // void, after succesful mutation
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    app: {
      client: { id: clientId, alias, writeAccess, editable },
      project: { id: projectId },
      sessionId,
    },
  } = useContext(AppContext);
  const isApplet = questionList.itemExtra.interactionType === 2;
  /** Button and menu options */
  const menuOptions = {
    send: {
      id: "send",
      icon: <SendOutlinedIcon fontSize="inherit" />,
      disabled: !writeAccess || !editable,
    },
    schedule: {
      id: "schedule",
      icon: <DateRangeOutlinedIcon fontSize="inherit" />,
      disabled: !writeAccess || !editable,
    },
    install: {
      id: "install",
      icon: <TouchAppOutlinedIcon fontSize="inherit" />,
      disabled: !writeAccess || !editable,
    },
    previewOnMyPhone: {
      id: "previewOnMyPhone",
      icon: <VisibilityOutlinedIcon fontSize="inherit" />,
      disabled: false,
    },
  };
  // Interactiontypes of surveys (0 = survey, 1 = button, 2 = app)
  const interactionTypes = useInteractionTypes();
  const button =
    interactionTypes?.[questionList.itemExtra.interactionType] ??
    interactionTypes[0];
  // option menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = Boolean(menuAnchor);
  const handleButtonClick = (event) => {
    onButtonClick();
    // prevent using the survey because it contains errors
    if (isError) {
      onError();
      return;
    }
    setMenuAnchor(event.currentTarget);
  };
  const handleItemClick = (optionId) => {
    onItemClick(optionId);
    handleMenuClose();
    if (optionId === "previewOnMyPhone") {
      previewMutation.mutate();
      return;
    }
    if (optionId === "install") {
      setRenameDialog({ open: true });
      return;
    }
    handleSave(optionId);
  };
  const handleMenuClose = () => {
    setMenuAnchor(false);
  };

  const handleSave = (optionId, qList = questionList) => {
    // conflicts can occur if the PUBLIC interaction (interactionId is typeof number) already exists for the client (itemId exists in local library)
    if (
      typeof interactionId === "number" &&
      interactions.find((interaction) => interaction.id === qList.itemId)
    ) {
      setOpenConflicDialog({
        open: true,
        optionId: optionId,
        questionList: qList,
      });
      return;
    }
    saveMutation.mutate({
      data: {
        clientId,
        // save a public or new local interaction as a new one. Edit a local interaction
        interactionId:
          typeof interactionId === "number" || interactionId === "new"
            ? "new"
            : questionList.itemId,
        interactionJSON: JSON.stringify(qList),
        install: ["send", "install"].includes(optionId),
        projectId,
        sessionId,
      },
      type: optionId,
    });
  };

  // use as button
  const [renameDialog, setRenameDialog] = useState({ open: false });
  const renameFunction = (name) => {
    const itemExtra = isApplet
      ? {
          interactionType: 2,
          appletName: name,
          appletLabel: "app",
        }
      : {
          interactionType: 1,
          buttonText: name,
          buttonLabel: "button",
        };
    handleSave("install", {
      ...questionList,
      itemExtra: {
        ...questionList.itemExtra,
        ...itemExtra,
      },
    });
  };

  // Server state
  const {
    accountSettings: { testDeviceConnectionId },
  } = useAccountSettings();
  const { interactions, refetchInteractions } = useInteraction();
  const { scheduler } = useScheduler({ id: clientId });
  // Muations
  const scheduleMutation = useMutation(() =>
    api.postBeepSchedule({
      clientId: clientId,
      beepSchedule: [
        ...(scheduler?.data ?? []),
        {
          itemId: questionList.itemId,
          startTime: moment().format(dateFormat),
          endTime: moment().add(2, "hours").format(dateFormat),
          randomizationScheme: 0,
          beepId: "MCuyRGsSewRcuzxc",
          reminderIntervals: null,
          useAsButton: 0,
          buttonText: null,
          buttonLabel: null,
          singleUse: 0,
          scheduleType: null,
        },
      ],
      sessionId: sessionId,
      projectId: projectId,
    })
  );

  const previewMutation = useMutation(() =>
    api.postPreviewInteraction({
      connectionId: testDeviceConnectionId,
      questionList: JSON.stringify([questionList]),
      useAsButton: 0,
      buttonText: null,
      buttonLabel: null,
      singleUse: 0,
      questionListName: questionList.fullQuestion,
      projectId,
      sessionId,
    })
  );

  const saveMutation = useMutation(
    ({ data }) => api.postLocalInteraction(data),
    {
      onSuccess: (data, { type }) => {
        if (data.data?.status > 0) {
          onSuccess(data.data);
          if (from === "editor")
            history.replace({
              state: { interactionId: questionList.itemId, interaction: null },
            });
          if (type === "schedule") {
            history.push({ pathname: "/scheduler", from });
          }
          if (conflictDialog.open) {
            // close the conflict dialog
            setOpenConflicDialog((prev) => ({ ...prev, open: false }));
          }
          if (renameDialog.open) {
            // close the rename dialog
            setRenameDialog((prev) => ({ ...prev, open: false }));
          }
          if (type === "send") {
            scheduleMutation.mutate();
          }
          refetchInteractions();
        }
      },
    }
  );

  // save conflict
  const [conflictDialog, setOpenConflicDialog] = useState({
    open: false,
    optionId: "",
    questionList: questionList,
  });

  if (clientId < 0) return null;
  const getSaveLoadingMessage = () => {
    switch (saveMutation?.variables?.type) {
      case "send":
        return t("common.notifications.saveLoading");
      case "install":
        return t("common.notifications.sendLoading");
      case "schedule":
        return t("common.notifications.saveLoading");
      default:
        return t("common.notifications.loading");
    }
  };
  const getSaveSuccessMessage = () => {
    switch (saveMutation?.variables?.type) {
      case "send":
        return t("common.notifications.loading");
      case "install":
        return t("common.notifications.sendSuccess");
      case "schedule":
        return t("common.notifications.saveSuccess");
      default:
        return t("common.notifications.saveSuccess");
    }
  };
  return (
    <React.Fragment>
      <MutationMessage
        mutation={previewMutation}
        text={{
          loading: t("common.notifications.sendLoading"),
          /*  success: `Sent to ${
            previewMutation.variables?.type === "preview" ? alias : "my phone"
          }`, */
          success: t("common.notifications.sendSuccess"),
        }}
      />
      <MutationMessage
        mutation={saveMutation}
        text={{
          loading: getSaveLoadingMessage(),
          success: getSaveSuccessMessage(),
        }}
      />
      <MutationMessage
        mutation={scheduleMutation}
        text={{
          loading: t("common.notifications.sendLoading"),
          success: t("common.notifications.sendSuccess"),
        }}
      />
      <RenameDialog
        open={renameDialog.open}
        setOpen={setRenameDialog}
        renameFunction={renameFunction}
        input={
          isApplet
            ? questionList?.itemExtra?.appletName ?? ""
            : questionList?.itemExtra?.buttonText ?? ""
        }
        dialogTitle={
          isApplet
            ? t("interactions.appDialog.title")
            : t("interactions.buttonDialog.title")
        }
        dialogContentText={
          isApplet
            ? t("interactions.appDialog.text")
            : t("interactions.buttonDialog.text")
        }
        dialogActionCancel={t("common.actions.cancel")}
        dialogActionConfirm={
          isApplet
            ? t("interactions.appDialog.confirm")
            : t("interactions.buttonDialog.confirm")
        }
      />
      <ConflictDialog
        open={conflictDialog.open}
        optionId={conflictDialog.optionId}
        questionList={conflictDialog.questionList}
        setOpen={setOpenConflicDialog}
        saveMutation={saveMutation}
        from={from}
      />

      {from === "library" ? (
        <Fab
          variant="extended"
          color="primary"
          aria-label="add"
          onClick={handleButtonClick}
        >
          {button.title}
        </Fab>
      ) : (
        <Button
          aria-label="use survey"
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleButtonClick}
        >
          {button.title}
        </Button>
      )}
      <Menu
        id="menu-library-use-survey"
        anchorEl={menuAnchor}
        anchorOrigin={
          from === "editor"
            ? {
                vertical: "bottom",
                horizontal: "right",
              }
            : {
                vertical: "bottom",
                horizontal: "left",
              }
        }
        transformOrigin={
          from === "editor"
            ? {
                vertical: "top",
                horizontal: "right",
              }
            : {
                vertical: "top",
                horizontal: "left",
              }
        }
        keepMounted
        open={openMenu}
        onClose={handleMenuClose}
      >
        <Typography
          disabled
          sx={{
            fontWeight: "500",
            fontSize: "0.8rem",
            paddingLeft: 2,
            paddingTop: 1,
            paddingBottom: 1,
          }}
        >
          {t("interactions.subtitle", {
            client: `"${trimString(alias, 15)}"`,
          })}
        </Typography>
        {button.menuOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleItemClick(option.id)}
            disabled={menuOptions[option.id].disabled}
          >
            <Box display="flex" sx={{ gap: 2, alignItems: "center" }}>
              <Box
                sx={{
                  minWidth: 6,
                  fontSize: 25,
                  color: "text.secondary",
                }}
              >
                {menuOptions[option.id].icon}
              </Box>

              <Box sx={{ whiteSpace: "normal", maxWidth: "300px" }}>
                <Typography variant="body2">{option.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.subTitle}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
}

export default UseSurveyButton;
